import type {
  ChartDataProvider,
  DashboardCreditNotesRow,
  DashboardDispatchedRow,
  DashboardPurchasingRow,
  DashboardReturnsRow,
  DashboardSalesRow,
  DashboardToShipRow,
  StandardSeriesKeys,
} from './DashboardTypes';
import {
  getRollingDashboardLabelForDate,
  getRollingDashboardLabels,
  zeroes,
} from './DashboardHelpers';
import { ONLINE_WAREHOUSE_IDS } from 'src/utils/enviromentValues';
import { isOnlineWarehouse, isStoreWarehouse } from './DashboardModules';
import { inferSalesChannelFromOrderChannel } from './DashboardCards';

type DashboardSalesMarginsRow = {
  SalesDate?: string | Date | null;
  OrderChannel?: string | null;
  ContributionMargin?: number | null;
  ContributionMarginRatio?: number | null;
};

type SalesMarginSeriesKeys =
  | 'salesMarginsStore'
  | 'salesMarginsOnline'
  | 'salesMarginRevenueStore'
  | 'salesMarginRevenueOnline';

type BucketKey = StandardSeriesKeys | SalesMarginSeriesKeys;

const BUCKET_KEYS: BucketKey[] = [
  'ordersStore',
  'ordersOnline',
  'ordersValueStore',
  'ordersValueOnline',
  'purchasesValueStore',
  'purchasesValueOnline',
  'toShipStore',
  'toShipOnline',
  'shippedStore',
  'shippedOnline',
  'returnsStore',
  'returnsOnline',
  'creditNotesStore',
  'creditNotesOnline',
  'creditNotesValueStore',
  'creditNotesValueOnline',
  'salesMarginsStore',
  'salesMarginsOnline',
  'salesMarginRevenueStore',
  'salesMarginRevenueOnline',
];


const sumArr = (a: number[], b: number[]) => a.map((v, i) => v + (b[i] ?? 0));
const createEmptyBuckets = (length: number): Record<BucketKey, number[]> =>
  Object.fromEntries(BUCKET_KEYS.map(key => [key, zeroes(length)])) as Record<BucketKey, number[]>;
const withBuckets = (length: number, values: Partial<Record<BucketKey, number[]>>): Record<BucketKey, number[]> => ({
  ...createEmptyBuckets(length),
  ...values,
});

const inferSalesChannelFromOrderChannelType = (orderChannelType: number): 'Store' | 'Online' | null => {
  if (orderChannelType === 1) return 'Online';
  if (orderChannelType === 2) return 'Online';
  if (orderChannelType === 3) return 'Store';
  return null;
};


export function buildDashboardSalesProvider(sales: DashboardSalesRow[] = []): ChartDataProvider {
  return {
    getSalesRepSeries: () => ({}),
    getStandardBuckets: (req) => {
      const labels = getRollingDashboardLabels(req.period);
      const byLabel = new Map<string, { ordersOnline: number; ordersStore: number; ordersValueOnline: number; ordersValueStore: number }>();
      labels.forEach(label => byLabel.set(label, { ordersOnline: 0, ordersStore: 0, ordersValueOnline: 0, ordersValueStore: 0 }));

      sales.forEach(row => {
        const label = getRollingDashboardLabelForDate(row.SalesDate, req.period, labels);
        if (!label) return;

        const channel = inferSalesChannelFromOrderChannelType(Number(row.OrderChannelType));
        if (!channel) return;

        const bucket = byLabel.get(label)!;
        const count = Number(row.Count ?? 0) || 0;
        const valueNet = Number(row.TotalAmountNet ?? 0) || 0;

        if (channel === 'Online') {
          bucket.ordersOnline += count;
          bucket.ordersValueOnline += valueNet;
        } else {
          bucket.ordersStore += count;
          bucket.ordersValueStore += valueNet;
        }
      });

      const pick = <K extends keyof { ordersOnline: number; ordersStore: number; ordersValueOnline: number; ordersValueStore: number }>(key: K) =>
        labels.map(label => byLabel.get(label)?.[key] ?? 0);

      return withBuckets(labels.length, {
        ordersStore: pick('ordersStore'),
        ordersOnline: pick('ordersOnline'),
        ordersValueStore: pick('ordersValueStore'),
        ordersValueOnline: pick('ordersValueOnline'),
      });
    },
  };
}

export function buildDashboardReturnsProvider(returns: DashboardReturnsRow[] = []): ChartDataProvider {
  return {
    getSalesRepSeries: () => ({}),
    getStandardBuckets: (req) => {
      const labels = getRollingDashboardLabels(req.period);
      const byLabel = new Map<string, number>();
      labels.forEach(label => byLabel.set(label, 0));

      returns.forEach(row => {
        const label = getRollingDashboardLabelForDate(row.ReturnCreateDate, req.period, labels);
        if (!label) return;
        byLabel.set(label, (byLabel.get(label) ?? 0) + (Number(row.Count ?? 0) || 0));
      });

      return withBuckets(labels.length, {
        returnsOnline: labels.map(label => byLabel.get(label) ?? 0),
      });
    },
  };
}

export function buildDashboardDispatchedProvider(dispatched: DashboardDispatchedRow[] = []): ChartDataProvider {
  return {
    getSalesRepSeries: () => ({}),
    getStandardBuckets: (req) => {
      const labels = getRollingDashboardLabels(req.period);
      const byLabel = new Map<string, { shippedOnline: number; shippedStore: number }>();
      labels.forEach(label => byLabel.set(label, { shippedOnline: 0, shippedStore: 0 }));

      dispatched.forEach(row => {
        const label = getRollingDashboardLabelForDate(row.Day, req.period, labels);
        if (!label) return;

        const bucket = byLabel.get(label)!;
        const count = Number(row.CountOrders ?? 0) || 0;
        if (isOnlineWarehouse(Number(row.WarehouseId))) bucket.shippedOnline += count;
        if (isStoreWarehouse(Number(row.WarehouseId))) bucket.shippedStore += count;
      });

      return withBuckets(labels.length, {
        shippedStore: labels.map(label => byLabel.get(label)?.shippedStore ?? 0),
        shippedOnline: labels.map(label => byLabel.get(label)?.shippedOnline ?? 0),
      });
    },
  };
}

export function buildDashboardToShipProvider(toShip: DashboardToShipRow[] = []): ChartDataProvider {
  return {
    getSalesRepSeries: () => ({}),
    getStandardBuckets: (req) => {
      const labels = getRollingDashboardLabels(req.period);
      const byLabel = new Map<string, { toShipOnline: number; toShipStore: number }>();
      labels.forEach(label => byLabel.set(label, { toShipOnline: 0, toShipStore: 0 }));

      toShip.forEach(row => {
        const label = getRollingDashboardLabelForDate(row.Day, req.period, labels);
        if (!label) return;

        const bucket = byLabel.get(label)!;
        const count = Number(row.CountOrders ?? 0) || 0;
        if (isOnlineWarehouse(Number(row.WarehouseId))) bucket.toShipOnline += count;
        if (isStoreWarehouse(Number(row.WarehouseId))) bucket.toShipStore += count;
      });

      return withBuckets(labels.length, {
        toShipStore: labels.map(label => byLabel.get(label)?.toShipStore ?? 0),
        toShipOnline: labels.map(label => byLabel.get(label)?.toShipOnline ?? 0),
      });
    },
  };
}

export function buildDashboardPurchasingProvider(purchasing: DashboardPurchasingRow[] = []): ChartDataProvider {
  return {
    getSalesRepSeries: () => ({}),
    getStandardBuckets: (req) => {
      const labels = getRollingDashboardLabels(req.period);
      const byLabel = new Map<string, { purchasesValueOnline: number; purchasesValueStore: number }>();
      labels.forEach(label => byLabel.set(label, { purchasesValueOnline: 0, purchasesValueStore: 0 }));

      purchasing.forEach(row => {
        const label = getRollingDashboardLabelForDate(row.PurchaseOrderCreated, req.period, labels);
        if (!label) return;
        byLabel.get(label)!.purchasesValueOnline += Number(row.OrderValueInDefaultCurreny ?? 0) || 0;
      });

      return withBuckets(labels.length, {
        purchasesValueStore: labels.map(label => byLabel.get(label)?.purchasesValueStore ?? 0),
        purchasesValueOnline: labels.map(label => byLabel.get(label)?.purchasesValueOnline ?? 0),
      });
    },
  };
}

export function buildDashboardCreditNotesProvider(creditNotes: DashboardCreditNotesRow[] = []): ChartDataProvider {
  return {
    getSalesRepSeries: () => ({}),
    getStandardBuckets: (req) => {
      const labels = getRollingDashboardLabels(req.period);
      const byLabel = new Map<string, { creditNotesOnline: number; creditNotesValueOnline: number }>();
      labels.forEach(label => byLabel.set(label, { creditNotesOnline: 0, creditNotesValueOnline: 0 }));

      creditNotes.forEach(row => {
        const label = getRollingDashboardLabelForDate(row.ReleaseDate, req.period, labels);
        if (!label) return;

        const bucket = byLabel.get(label)!;
        bucket.creditNotesOnline += Number(row.Count ?? 0) || 0;
        bucket.creditNotesValueOnline += Number(row.SumTotalAmountNet ?? 0) || 0;
      });

      return withBuckets(labels.length, {
        creditNotesStore: zeroes(labels.length),
        creditNotesOnline: labels.map(label => byLabel.get(label)?.creditNotesOnline ?? 0),
        creditNotesValueStore: zeroes(labels.length),
        creditNotesValueOnline: labels.map(label => byLabel.get(label)?.creditNotesValueOnline ?? 0),
      });
    },
  };
}

export function buildDashboardSalesMarginsProvider(salesMargins: DashboardSalesMarginsRow[] = []): ChartDataProvider {
  return {
    getSalesRepSeries: () => ({}),
    getStandardBuckets: (req) => {
      const labels = getRollingDashboardLabels(req.period);
      const byLabel = new Map<string, { salesMarginsOnline: number; salesMarginsStore: number; salesMarginRevenueOnline: number; salesMarginRevenueStore: number }>();
      labels.forEach(label => byLabel.set(label, { salesMarginsOnline: 0, salesMarginsStore: 0, salesMarginRevenueOnline: 0, salesMarginRevenueStore: 0 }));

      salesMargins.forEach(row => {
        const label = getRollingDashboardLabelForDate(row.SalesDate, req.period, labels);
        if (!label) return;

        const channel = inferSalesChannelFromOrderChannel(row.OrderChannel);
        if (!channel) return;

        const bucket = byLabel.get(label)!;
        const contributionMargin = Number(row.ContributionMargin ?? 0) || 0;
        const contributionMarginRatio = Number(row.ContributionMarginRatio ?? 0) || 0;
        const impliedRevenue = contributionMarginRatio > 0 ? contributionMargin / contributionMarginRatio : 0;

        if (channel === 'Online') {
          bucket.salesMarginsOnline += contributionMargin;
          bucket.salesMarginRevenueOnline += impliedRevenue;
        } else {
          bucket.salesMarginsStore += contributionMargin;
          bucket.salesMarginRevenueStore += impliedRevenue;
        }
      });

      return withBuckets(labels.length, {
        salesMarginsStore: labels.map(label => byLabel.get(label)?.salesMarginsStore ?? 0),
        salesMarginsOnline: labels.map(label => byLabel.get(label)?.salesMarginsOnline ?? 0),
        salesMarginRevenueStore: labels.map(label => byLabel.get(label)?.salesMarginRevenueStore ?? 0),
        salesMarginRevenueOnline: labels.map(label => byLabel.get(label)?.salesMarginRevenueOnline ?? 0),
      });
    },
  };
}

export function composeProviders(...providers: ChartDataProvider[]): ChartDataProvider {
  return {
    getSalesRepSeries: (req, labels) => {
      const acc: Record<string, number[]> = {};
      providers.forEach(provider => {
        const part = provider.getSalesRepSeries(req, labels) || {};
        Object.entries(part).forEach(([repId, data]) => {
          acc[repId] = acc[repId] ? sumArr(acc[repId], data) : [...data];
        });
      });
      return acc;
    },
    getStandardBuckets: (req, labels) => {
      const base = createEmptyBuckets(labels.length);
      providers.forEach(provider => {
        const part = provider.getStandardBuckets(req, labels) as Record<BucketKey, number[]>;
        BUCKET_KEYS.forEach(key => {
          base[key] = sumArr(base[key], part?.[key] ?? zeroes(labels.length));
        });
      });
      return base;
    },
  };
}